import React, { useState, useContext, useCallback, useRef } from 'react'
import { omit } from 'lodash'
import { ExtendedFormControlProps } from '~/design_system'

type ItemWithRef = {
  id: string
  el: HTMLElement
}

type FieldAlert = {
  id: string
  label?: string
  type: 'error' | 'warning'
  message: string
}

const FormAlertListContext = React.createContext<
  | {
      targets: Record<string, ItemWithRef>
      registerItem: (id: string, el: HTMLElement) => void
      unregisterItem: (id: string) => void
      alerts: Array<FieldAlert>
      refreshAlert: (id: string) => void
    }
  | undefined
>(undefined)

/**
 * See: https://storybook.beta.vndly.com/?path=/docs/components-form-alert-list--docs
 */
export const FormAlertListContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [targets, setTargets] = useState<Record<string, ItemWithRef>>({})
  const [alerts, setAlerts] = useState<Array<FieldAlert>>([])

  const registerItem = useCallback((id: string, el: HTMLElement) => {
    const item = {
      id,
      el
    }

    setTargets(m => {
      if (m[item.id]) {
        return m
      }
      return { ...m, [id]: item }
    })
  }, [])

  const unregisterItem = useCallback(
    (id: string) => {
      setTargets(m => omit(m, [id]))
    },
    [setTargets]
  )

  const refreshAlert = useCallback(
    (id: string) => {
      const item = targets[id]
      if (!item) return
      // Set all registered item alerts. These alerts will be used to display them
      // inside the Form Alert List banner.
      setAlerts(m => {
        const label = item.el?.querySelector(`label[for="${item.id}"]`)?.textContent ?? ''
        const error = item.el?.querySelector(`div[id="${item.id}-feedback"]`)?.textContent ?? ''
        const warning = item.el?.querySelector(`div[id="${item.id}-warning"] span`)?.textContent ?? ''
        const existing = m.find(i => i.id === item.id)
        // If the alert already exists and is still erroring do not add it again
        if (existing && (error || warning)) {
          return m
        }
        // Previous error has been cleared so clear the alert
        if (existing && !error && !warning) {
          return m.filter(i => i.id !== item.id)
        }

        let newAlerts = m
        if (error !== '') {
          newAlerts = [...newAlerts, { id: item.id, label, type: 'error', message: error }]
        }
        if (warning !== '') {
          newAlerts = [...newAlerts, { id: item.id, label, type: 'warning', message: warning }]
        }
        return newAlerts
      })
    },
    [targets, setAlerts]
  )

  return (
    <FormAlertListContext.Provider value={{ targets, registerItem, unregisterItem, alerts, refreshAlert }}>
      {children}
    </FormAlertListContext.Provider>
  )
}

export function useFormAlertListContext() {
  return useContext(FormAlertListContext)
}

export function useFormAlertRegistrationRef(props: ExtendedFormControlProps) {
  const ctx = useFormAlertListContext()
  const { id } = props
  const ref = useRef()

  React.useEffect(() => {
    if (ref.current && id && ctx?.registerItem) {
      ctx?.registerItem(id, ref.current)
    }
    return () => {
      if (id && ctx?.unregisterItem) {
        ctx?.unregisterItem(id)
      }
    }
  }, [])

  return ref
}
